/**
 * Shared constants & helper functions for the Agent Management Dashboard.
 *
 * KNOWN_AGENT_COLORS / KNOWN_AGENT_NAMES are seed maps for agents we've
 * explicitly branded.  For any agent NOT in these maps the helpers below
 * generate a deterministic color from a palette and title-case the ID.
 * This means agents added to agents.json automatically get reasonable
 * display treatment without needing a frontend code change.
 */

/**
 * Authenticated fetch — injects the OIDC Bearer token so admin API calls
 * work in environments with Easy Auth / OIDC middleware.
 *
 * On a 401 it renews the session silently and retries once with the new
 * token. getIdToken() only renews when the cached user has expired, so a
 * token the backend rejects before then (e.g. an ID token whose own expiry
 * falls before the session's) used to fail every call until a full reload --
 * about a third of Prod's agent-interactions loads after CR12739. A 401 means
 * the request was not processed, so retrying it is safe for writes too.
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  let oidc: { refreshToken(): Promise<{ id_token?: string; access_token?: string } | null> } | null = null;
  let token: string | null = null;
  try {
    const { default: OidcAuthService } = await import('../../services/oidcAuth');
    const service = OidcAuthService.getInstance();
    await service.ensureInitialized();
    oidc = service;
    token = await service.getIdToken() || await service.getAccessToken();
  } catch {
    // Token unavailable — continue without (will rely on cookies/Easy Auth)
  }

  const send = (bearer: string | null) => {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> || {}),
    };
    if (bearer) {
      headers['Authorization'] = `Bearer ${bearer}`;
    }
    return fetch(url, { ...init, credentials: 'include', headers });
  };

  const response = await send(token);
  if (response.status !== 401 || !oidc) {
    return response;
  }

  let renewed: string | null = null;
  try {
    const user = await oidc.refreshToken();
    renewed = user?.id_token || user?.access_token || null;
  } catch {
    // Renewal failed — hand back the original 401
  }
  if (!renewed || renewed === token) {
    return response;
  }
  return send(renewed);
}

// ============================================================================
// SEED MAPS — known agents with explicit colors / names
// ============================================================================

const KNOWN_AGENT_COLORS: Record<string, string> = {
  'aldo': '#2b2e32',
  'hrpolicy': '#8b5cf6',
  'orientation': '#ec4899',
  'unifirst-docs': '#10b981',
  'direct-embroidery': '#f97316',
  'pwms-support': '#f97316',
  'product': '#3b82f6',
  'iris': '#6f747b',
  'service-desk': '#d946ef',
  'msgraph': '#0078d4',
  'firstaid-safety': '#ef4444',
  'archimedes': '#14b8a6',
  'voucher': '#a855f7',
  'erp-faq': '#0ea5e9',
  'customer360': '#f59e0b',
  'customer360-gpt': '#f59e0b',
  'all': '#9da2a8',
  'unassigned': '#6f747b'
};

const KNOWN_AGENT_NAMES: Record<string, string> = {
  'aldo': 'Scout',
  'hrpolicy': 'PolicyTech',
  'orientation': 'Orientation',
  'unifirst-docs': 'UniFirst Docs',
  'direct-embroidery': 'Direct Embroidery',
  'pwms-support': 'PWMS Support',
  'product': 'Product Searchlight',
  'iris': 'IRIS',
  'service-desk': 'Service Desk',
  'msgraph': 'MS Graph',
  'firstaid-safety': 'First Aid & Safety',
  'archimedes': 'Archimedes',
  'voucher': 'Voucher Assistant',
  'erp-faq': 'ERP Voucher',
  'customer360': 'Customer 360',
  'customer360-gpt': 'Customer 360 (GPT)'
};

export const AGENT_COLORS = KNOWN_AGENT_COLORS;
export const AGENT_NAMES = KNOWN_AGENT_NAMES;

export const AGENT_ID_ALIASES: Record<string, string> = {
  'direct-embroidery-metadata': 'direct-embroidery',
  'service-desk-chunks': 'service-desk',
  'unifirst_docs': 'unifirst-docs',
  'direct_embroidery': 'direct-embroidery',
  'pwms_support': 'pwms-support',
  'service_desk': 'service-desk',
  'erp_faq': 'erp-faq',
  'firstaid_safety': 'firstaid-safety',
};

export const getCanonicalAgentId = (agentId: string): string => {
  if (AGENT_ID_ALIASES[agentId]) return AGENT_ID_ALIASES[agentId];
  return agentId.replace(/_/g, '-');
};

export const getAgentDisplayName = (agentId: string): string => {
  const canonicalId = getCanonicalAgentId(agentId);
  if (KNOWN_AGENT_NAMES[canonicalId]) return KNOWN_AGENT_NAMES[canonicalId];
  // Title-case the dash-separated ID as fallback
  return canonicalId
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const getAgentGroup = (displayName: string): string => {
  const parenMatch = displayName.match(/^(.+?)\s*\(/);
  if (parenMatch) {
    return parenMatch[1].trim();
  }
  return displayName;
};

const DYNAMIC_PALETTE = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6',
  '#ef4444', '#0ea5e9', '#22c55e', '#e879f9', '#f97316',
  '#06b6d4', '#a3e635', '#fb923c', '#818cf8', '#34d399',
];

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export const getAgentColor = (agentIdOrName: string): string => {
  const id = getCanonicalAgentId(agentIdOrName.toLowerCase());
  if (KNOWN_AGENT_COLORS[id]) return KNOWN_AGENT_COLORS[id];

  for (const [key, name] of Object.entries(KNOWN_AGENT_NAMES)) {
    if (name.toLowerCase() === agentIdOrName.toLowerCase()) {
      return KNOWN_AGENT_COLORS[key] || '#6f747b';
    }
  }

  // Deterministic color from palette for unknown agents
  return DYNAMIC_PALETTE[hashCode(id) % DYNAMIC_PALETTE.length];
};

export const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// API BASE URL
// ============================================================================

export const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

// ============================================================================
// AGENT BLOB MAPPING (used by KnowledgeBaseTab copy utility)
// ============================================================================

export const AGENT_BLOB_MAPPING: Record<string, {
  containers: { name: string; label: string; type: 'source' | 'images' }[];
  sharepointFolder: string;
}> = {
  'hrpolicy': {
    containers: [{ name: 'agent-hrpolicy-phase1', label: 'HR Policy PDFs', type: 'source' }],
    sharepointFolder: 'PolicyTech'
  },
  'orientation': {
    containers: [{ name: 'agent-orientation', label: 'Orientation PDFs', type: 'source' }],
    sharepointFolder: 'Orientation'
  },
  'direct-embroidery': {
    containers: [
      { name: 'agent-owensboro2-directembroidery', label: 'Training Manuals (PDFs)', type: 'source' },
      { name: 'training-manual-images', label: 'Training Manual Images', type: 'images' }
    ],
    sharepointFolder: 'Owensboro Production'
  },
  'pwms-support': {
    containers: [{ name: 'agent-pwms', label: 'PWMS Docs', type: 'source' }],
    sharepointFolder: 'PWMS'
  },
  'product': {
    containers: [{ name: 'agent-product-images', label: 'Product Images', type: 'images' }],
    sharepointFolder: 'Product Searchlight'
  },
  'iris': {
    containers: [{ name: 'askiris-images', label: 'IRIS Images', type: 'images' }],
    sharepointFolder: 'IRIS'
  },
  'unifirst-docs': {
    containers: [{ name: 'unifirst-docs-images', label: 'UniFirst Docs Images', type: 'images' }],
    sharepointFolder: 'UniFirst Docs'
  },
  'service-desk': {
    containers: [{ name: 'ivanti-docs-images', label: 'Ivanti Docs Images', type: 'images' }],
    sharepointFolder: 'Service Desk'
  }
};

